import styled, { css } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import translate from '@i18n'
import IconButton from '@components/Buttons/IconButton'
import { chatbotRevealAnimation } from '@components/Chatbot/loading/QuestionLoadedAnimation'
import ReponsiveImageV2 from '@components/ResponsiveImageV2'

import { AnswerContentType, SourceType } from '../types'

import type { FunctionComponent } from 'react'

type AnswerParagraphProps = {
  contentItem: AnswerContentType
  onSourceLinkClick?: (linkItem: SourceType) => void
  linkItem?: SourceType
  category?: string
  contentImage?: SourceType['image']
}

const ParagraphWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing8};
  `}
`

const StyledAnswerCategory = styled.h2`
  ${({
    theme: {
      typography: {
        headings: { small: smallHeadings },
      },
    },
  }) => css`
    ${smallHeadings};
    margin: 0;
    text-transform: uppercase;
  `}
`

const StyledParagraph = styled.p`
  ${({
    theme: {
      typography: {
        bodycopy: {
          medium2: { bundledCSS: medium2BodycopyCSS },
        },
      },
    },
  }) => css`
    ${medium2BodycopyCSS};
    margin: 0;
    min-height: 16px;
    white-space: pre-wrap; // This allows line breaks in the text
  `}
`

const StyledResponsiveImageWrapper = styled.div`
  ${() => css`
    width: 632px;
    max-width: 632px;
    margin: 0 auto;

    ${chatbotRevealAnimation}

    ${mobileCSS(css`
      width: 100%;
    `)};
  `}
`

const SourceIconButton = styled(IconButton)`
  ${({
    theme: {
      colors: { fillStrong },
    },
  }) => css`
    background-color: ${fillStrong};
    border-radius: 50%;
    margin-left: 2px;
  `}
`

const AnswerParagraph: FunctionComponent<AnswerParagraphProps> = ({
  contentItem,
  linkItem,
  contentImage,
  category,
  onSourceLinkClick,
}) => {
  return (
    <>
      <ParagraphWrapper>
        {category && <StyledAnswerCategory>{category}</StyledAnswerCategory>}
        <StyledParagraph>
          {contentItem.text}
          {linkItem && (
            <SourceIconButton
              type="button"
              ariaLabel={translate('aiSearch.answerMessage.sources')}
              buttonSize={20}
              iconName="link"
              iconSize={12}
              onClick={() => onSourceLinkClick?.(linkItem)}
            />
          )}
        </StyledParagraph>
      </ParagraphWrapper>
      {contentImage && (
        <StyledResponsiveImageWrapper>
          <ReponsiveImageV2
            alt={contentImage.alt}
            src={contentImage.src}
            crops={contentImage.crops}
            isLazy={false}
            widths={{ desktop: 326, tablet: 326, mobile: 326 }}
            ratios={{
              desktop: '3_2',
              tablet: '3_2',
              mobile: '3_2',
            }}
          />
        </StyledResponsiveImageWrapper>
      )}
    </>
  )
}

export default AnswerParagraph
